import { Logger } from 'src/latest/src/services';
import TagManager from 'react-gtm-module';

class AnalyticsService {
    // Initializing the Google Tag Manager
   initializeGoogleTagManager(clientAppConfig) {
        try {
            if (clientAppConfig.googleAdsTrackingId && clientAppConfig.googleAdsTrackingId !== "") {
                const tagManagerArgs = {
                    gtmId: clientAppConfig.googleAdsTrackingId,
                    dataLayer: {
                        userProjectId: clientAppConfig.tenantName,
                        environment: process.env.REACT_APP_ENVIRONMENT,
                    }
                };

                TagManager.initialize(tagManagerArgs);
                Logger.log("Google Tag Manager initialized successfully");
            }
        } catch (error) {
            Logger.error("Failed to initialize Google Tag Manager:", error);
        }
    }

    /**
     * Initializes the Meta Ads (Facebook Pixel) tracking script.
     *
     * @param {Object} clientAppConfig - The client application configuration object.
     * @param {String} clientAppConfig.metaAdsTrackingId - The Meta Ads tracking ID.
     * @returns {Boolean} - Returns `true` if the script is successfully initialized, `false` otherwise.
     */
    initializeMetaAdsScript(clientAppConfig) {
        try {
            if (!clientAppConfig?.metaAdsTrackingId || clientAppConfig.metaAdsTrackingId === '') {
                Logger.warn('Meta Ads tracking ID is missing or invalid');

                return false;
            }

            if (window.fbq) {
                Logger.log('Meta Ads script already initialized');

                return true;
            }

            (function(f, b, e, v, n, t, s) {
                if (f.fbq) return;

                n = f.fbq = function() {
                    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
                };

                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = true;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = true;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t, s);
            })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

            window.fbq('init', clientAppConfig.metaAdsTrackingId);

            const noscriptPixel = document.createElement('noscript');
            const pixelImg = document.createElement('img');

            pixelImg.height = '1';
            pixelImg.width = '1';
            pixelImg.style.display = 'none';
            pixelImg.src = `https://www.facebook.com/tr?id=${clientAppConfig.metaAdsTrackingId}&ev=PageView&noscript=1`;
            noscriptPixel.appendChild(pixelImg);
            document.head.appendChild(noscriptPixel);

            Logger.log('Meta Ads script initialized successfully');

            return true;
        } catch (error) {
            Logger.error('Failed to initialize Meta Ads script:', error);

            return false;
        }
    }

    /**
     * Initializes the TikTok Pixel tracking script.
     *
     * @param {Object} clientAppConfig - The client application configuration object.
     * @param {String} clientAppConfig.tiktokAdsTrackingId - The TikTok Pixel tracking ID.
     * @returns {Boolean} - Returns `true` if the script is successfully initialized, `false` otherwise.
     */
    initializeTikTokPixel(clientAppConfig) {
        try {
            if (!clientAppConfig?.tiktokAdsTrackingId || clientAppConfig.tiktokAdsTrackingId === '') {
                Logger.warn('TikTok Ads tracking ID is missing or invalid');

                return false;
            }

            if (window.ttq) {
                Logger.log('TikTok Pixel already initialized');

                return true;
            }

            ((w, d, t) => {
                w.TiktokAnalyticsObject = t;
                const ttq = w[t] = w[t] || [];

                ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];

                ttq.setAndDefer = (t, e) => {
                    t[e] = function() {
                        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                    };
                };

                for (let i = 0; i < ttq.methods.length; i++) {
                    ttq.setAndDefer(ttq, ttq.methods[i]);
                }

                ttq.instance = (pixelId) => {
                    const instance = ttq._i[pixelId] || [];
                    
                    for (let i = 0; i < ttq.methods.length; i++) {
                        ttq.setAndDefer(instance, ttq.methods[i]);
                    }

                    return instance;
                };

                ttq.load = (pixelId, options) => {
                    const scriptUrl = "https://analytics.tiktok.com/i18n/pixel/events.js";

                    ttq._i = ttq._i || {};
                    ttq._i[pixelId] = [];
                    ttq._i[pixelId]._u = scriptUrl;
                    ttq._t = ttq._t || {};
                    ttq._t[pixelId] = +new Date();
                    ttq._o = ttq._o || {};
                    ttq._o[pixelId] = options || {};

                    const script = d.createElement("script");

                    script.type = "text/javascript";
                    script.async = true;
                    script.src = scriptUrl + "?sdkid=" + pixelId + "&lib=" + t;

                    const firstScript = d.getElementsByTagName("script")[0];

                    firstScript.parentNode.insertBefore(script, firstScript);
                };

                ttq.load(clientAppConfig.tiktokAdsTrackingId);
            })(window, document, 'ttq');

            Logger.log('TikTok Pixel initialized successfully');

            return true;

        } catch (error) {
            Logger.error('Failed to initialize TikTok Pixel:', error);

            return false;
        }
    }

    /**
     * Initializes the Snapchat Pixel tracking script.
     *
     * @param {Object} clientAppConfig - The client application configuration object.
     * @param {String} clientAppConfig.snapchatAdsTrackingId - The Snapchat Pixel tracking ID.
     * @returns {Boolean} - Returns `true` if the script is successfully initialized, `false` otherwise.
     */
    initializeSnapPixel(clientAppConfig) {
        try {
            if (!clientAppConfig?.snapchatAdsTrackingId || clientAppConfig.snapchatAdsTrackingId === '') {
                Logger.warn('Snapchat Ads tracking ID is missing or invalid');

                return false;
            }

            if (window.snaptr) {
                Logger.log('Snapchat Pixel already initialized');

                return true;
            }

            ((e, t, n) => {
                if (e.snaptr) return;

                const a = e.snaptr = function() {
                    a.handleRequest ? a.handleRequest.apply(a, arguments) : a.queue.push(arguments);
                };

                a.queue = [];
                const s = 'script';
                const r = t.createElement(s);

                r.async = true;
                r.src = n;
                const u = t.getElementsByTagName(s)[0];

                u.parentNode.insertBefore(r, u);
            })(window, document, 'https://sc-static.net/scevent.min.js');

            window.snaptr('init', clientAppConfig.snapchatAdsTrackingId);
            
            Logger.log('Snapchat Pixel initialized successfully');

            return true;

        } catch (error) {
            Logger.error('Failed to initialize Snapchat Pixel:', error);

            return false;
        }
    }

   
}


export const analyticsService = new AnalyticsService();
